var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import cn from "classnames";
import "./severityIcons.scss";
import { exhaustiveMatchingGuard } from "../../helpers/typechecks/exhaustiveMatchingGuard";
import Icon from "@ant-design/icons";
/**
 * icon for the error severity.
 */
const CrossSvg = () => (_jsx("svg", { width: "100%", height: "100%", xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0.8 0.8 8.4 6.6", children: _jsx("path", { fill: "currentColor", d: "M9.064.315a.188.188 0 0 0-.187-.188L7.33.134 5 2.912 2.673.137 1.123.13a.187.187 0 0 0-.187.187c0 .045.017.087.045.122l3.05 3.633-3.05 3.63a.188.188 0 0 0 .143.31l1.549-.007L5 5.227l2.328 2.775 1.547.007a.187.187 0 0 0 .187-.187.195.195 0 0 0-.044-.122L5.973 4.07 9.023.437a.188.188 0 0 0 .041-.122Z" }) }));
/**
 * icon for the success severity.
 */
const CheckmarkSvg = () => (_jsx("svg", { width: "100%", height: "100%", xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 10 8", children: _jsx("path", { fill: "currentColor", d: "M9.382.273h-1.1a.746.746 0 0 0-.606.312L3.99 5.695 2.323 3.379a.75.75 0 0 0-.608-.312H.616a.188.188 0 0 0-.152.298l2.92 4.05a.745.745 0 0 0 1.212 0L9.532.57a.187.187 0 0 0-.15-.298Z" }) }));
/**
 * icon for the warning severity.
 */
const ExclamationSvg = () => (_jsx("svg", { width: "100%", height: "100%", xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 4 12", children: _jsx("path", { fill: "currentColor", d: "M.875 10.125a1.125 1.125 0 1 0 2.25 0 1.125 1.125 0 0 0-2.25 0ZM1.438 7.5h1.125a.188.188 0 0 0 .187-.188V.938A.188.188 0 0 0 2.562.75H1.438a.188.188 0 0 0-.188.188v6.375c0 .103.084.187.188.187Z" }) }));
/**
 * icon for the information severity.
 */
const InfoSvg = () => (_jsx("svg", { width: "100%", height: "100%", xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 4 12", children: _jsx("path", { fill: "currentColor", d: "M.875 1.875a1.125 1.125 0 1 0 2.25 0 1.125 1.125 0 0 0-2.25 0ZM2.563 4.5H1.437a.188.188 0 0 0-.187.188v6.375c0 .103.084.187.188.187h1.125a.188.188 0 0 0 .187-.188V4.689a.188.188 0 0 0-.188-.188Z" }) }));
/**
 * Displaying an icon based on its severity (success, info, warning or error).
 * Use these icons to indicate the severity of a message or alert.
 * The icons have great contrast, for increased accessibility.
 */
export function SeverityIcon(_a) {
    var { severity, size, alt } = _a, restProps = __rest(_a, ["severity", "size", "alt"]);
    switch (severity) {
        case "error":
            return _jsx(ErrorIcon, Object.assign({ size: size, alt: alt }, restProps));
        case "information":
            return _jsx(InformationIcon, Object.assign({ size: size, alt: alt }, restProps));
        case "warning":
            return _jsx(WarningIcon, Object.assign({ size: size, alt: alt }, restProps));
        case "success":
            return _jsx(SuccessIcon, Object.assign({ size: size, alt: alt }, restProps));
        default:
            return exhaustiveMatchingGuard(severity);
    }
}
/**
 * Renders an error icon.
 */
function ErrorIcon(props) {
    const { size } = props;
    const style = {
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: `${size}px`,
        borderWidth: `${size / 10}px`,
        padding: `${size / 6}px`
    };
    return (_jsx(Icon, Object.assign({}, props, { className: cn("severity-icon", "severity-icon--error"), style: style, component: CrossSvg })));
}
/**
 * Renders an information icon.
 */
function InformationIcon(props) {
    const { size } = props;
    const style = {
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: `${size}px`,
        borderWidth: `${size / 10}px`,
        padding: `${size / 8}px`
    };
    return (_jsx(Icon, Object.assign({}, props, { className: cn("severity-icon", "severity-icon--information"), style: style, component: InfoSvg })));
}
/**
 * Renders a warning icon.
 */
function WarningIcon(props) {
    const { size } = props;
    const style = {
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: `${size}px`,
        borderWidth: `${size / 10}px`,
        padding: `${size / 8}px`
    };
    return (_jsx(Icon, Object.assign({}, props, { className: cn("severity-icon", "severity-icon--warning"), style: style, component: ExclamationSvg })));
}
/**
 * Renders a success icon.
 */
function SuccessIcon(props) {
    const { size } = props;
    const style = {
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: `${size}px`,
        borderWidth: `${size / 10}px`,
        padding: `${size / 8}px`
    };
    return (_jsx(Icon, Object.assign({}, props, { className: cn("severity-icon", "severity-icon--success"), style: style, component: CheckmarkSvg })));
}
